import React, { ReactElement, useState } from "react"
import { TransportOrderTable } from "./TransportOrderTable/TransportOrderTable"
import { useLazyGetTransportOrdersAsAdminQuery } from "@/client/transportorder-hub/transportOrderHubApi"
import TableToolbar from "@rio-cloud/rio-uikit/TableToolbar"
import TableSearch from "@rio-cloud/rio-uikit/TableSearch"
import Notification from "@rio-cloud/rio-uikit/Notification"
import { FormattedMessage } from "react-intl"
import NotFoundState from "@rio-cloud/rio-uikit/NotFoundState"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { getSelectedAdminTransportOrder } from "@/redux/admin-ui.selector"
import { adminUiSlice } from "@/redux/admin-ui.slice"
import { AdminShipment, Shipment } from "@/model/transportorder/transportOrder"

export const TransportOrders = (): ReactElement => {
    const dispatch = useAppDispatch()
    const [triggerQuery, queryResult] = useLazyGetTransportOrdersAsAdminQuery()
    const selectedTransportOrder = useAppSelector(getSelectedAdminTransportOrder)
    const hasShipmentsData = queryResult.isSuccess && queryResult.data && queryResult.data.length > 0

    const onSearch = (identifier: string) => {
        if (identifier === undefined || identifier.trim() === "") {
            return
        }

        dispatch(adminUiSlice.actions.setSelectedTransportOrder(undefined))

        triggerQuery({ identifier })
            .unwrap()
            .then((transportOrders) => {
                dispatch(adminUiSlice.actions.setSelectedTransportOrder(transportOrders[0]))
            })
            .catch((err) => {
                Notification.error(`Unable to fetch transport orders: ${JSON.stringify(err)}`)
            })
    }

    const onTransportOrderSelected = (transportOrder: Shipment) => {
        dispatch(adminUiSlice.actions.setSelectedTransportOrder(transportOrder as AdminShipment))
    }

    return (
        <>
            <TableToolBar onSearch={onSearch}/>
            {hasShipmentsData ? (
                <TransportOrderTable
                    transportOrders={queryResult.data || []}
                    selectedTransportOrderId={selectedTransportOrder?.id}
                    onTransportOrderSelected={onTransportOrderSelected}
                />
            ) : (
                <NotFoundState
                    headline={<FormattedMessage id={"rio.admin.transportOrder.search.noResults.title"} />}
                    message={<FormattedMessage id={"rio.admin.transportOrder.search.noResults.message"} />}
                />
            )}
        </>
    )
}

interface TableToolBarProps {
    onSearch: (value: string) => void,
}

const TableToolBar = (props: TableToolBarProps): React.ReactElement => {
    const { onSearch } = props

    const [searchField, setSearchField] = useState("")

    return (
        <TableToolbar>
            <div className="table-toolbar-container">
                <div className="table-toolbar-group-left">
                    <div className="table-toolbar-column">
                        <div className="min-width-250">
                            <div className="form-control-feedback-wrapper">
                                <TableSearch
                                    value={searchField}
                                    onChange={newValue => setSearchField(newValue)}
                                    onKeyPress={(e) => {
                                        if (e.key === "Enter") {
                                            onSearch(searchField)
                                        }
                                    }}
                                    placeholder={"Search by identifier ..."}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </TableToolbar>
    )
}
